import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { signupUser } from './protected/services/auth';
import { getAuth, sendEmailVerification } from 'firebase/auth';
import { signInWithEmailAndPassword } from 'firebase/auth';
import mailIcon from '../../assets/icons/mail.png';
import passwordIcon from '../../assets/icons/padlock.png';
import showIcon from '../../assets/icons/unhide.png';
import hideIcon from '../../assets/icons/hide.png';

const Signup = () => {
  const [formData, setFormData] = useState({ email: '', password: '', con_pass: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (formData.password !== formData.con_pass) {
      setError('Passwords do not match');
      return;
    }

    if (!agreed) {
      setError('Please agree to the Terms and Conditions and Privacy Policy');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await signupUser(formData.email, formData.password);
      if (result.success) {
        setFormData({ email: '', password: '', con_pass: '' });
        
        // Show success message
        const successMessage = document.createElement('div');
        successMessage.className = 'text-green-400 text-sm text-center mt-4';
        successMessage.textContent = result.message;
        
        const form = document.querySelector('form');
        if (form) {
          form.parentNode.insertBefore(successMessage, form.nextSibling);
          setTimeout(() => {
            successMessage.remove();
          }, 5000);
        }

        // Clear any existing error messages
        setError('');
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const container = document.getElementById('circle-container');
    if (!container) return;

    container.innerHTML = '';
    const ellipses = [
      { width: '30vw', height: '30vw', left: '70vw', top: '5vh', background: 'linear-gradient(200.63deg, #CCE307 25.75%, #8946EA 77.76%)' },
      { width: '18vw', height: '18vw', left: '40vw', top: '8vh', background: 'linear-gradient(208.25deg, #CCE307 16.24%, #8946EA 36.77%)', transform: 'matrix(-1, 0, 0, 1, 0, 0)' },
      { width: '28vw', height: '28vw', left: '5vw', top: '45vh', background: 'linear-gradient(219.99deg, #CCE307 32.87%, #8146E2 68.9%)' },
      { width: '12vw', height: '12vw', left: '55vw', top: '70vh', background: 'linear-gradient(191.73deg, #CCE307 12.04%, #8946EA 75.7%)' }
    ];

    ellipses.forEach(({ width, height, left, top, background, transform }) => {
      const circle = document.createElement('div');
      Object.assign(circle.style, {
        position: 'absolute', width, height, left, top,
        background, borderRadius: '50%', zIndex: 0,
        pointerEvents: 'none', transform: transform || ''
      });
      container.appendChild(circle);
    });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0812] py-12 px-4 relative overflow-hidden" dir="ltr">
      <div id="circle-container" className="absolute inset-0 overflow-hidden z-0" />

      <div className="w-full relative z-10" style={{ maxWidth: '1000px' }}>
        <div
          className="backdrop-blur-2xl rounded-[32px] flex flex-col md:flex-row overflow-hidden shadow-xl border border-white/20"
          style={{
            background: 'linear-gradient(to bottom right, rgba(20, 20, 20, 0.2), rgba(40, 40, 40, 0.1))',
            minHeight: '500px'
          }}
        >
          {/* Left Side */}
          <div
            className="md:w-1/3 p-8 flex flex-col items-start border-b md:border-b-0 md:border-r border-white/40 relative"
            style={{
              borderRadius: '20px',
              background: 'linear-gradient(200.06deg, rgba(255, 255, 255, 0.8) 10.98%, rgba(255, 255, 255, 0.1) 95.69%)',
            }}
          >
            <h2
              className="font-extrabold text-black leading-snug relative mt-8 md:mt-5 whitespace-nowrap md:whitespace-normal"
              style={{
                fontFamily: 'Century Gothic',
                fontSize: 'clamp(1.5rem, 7vw, 2rem)',
                letterSpacing: '0.05em',
              }}
            >
              <span className="md:hidden">CREATE ACCOUNT</span>
              <span className="hidden md:block">
                CREATE<br />
                <span className="bg-gradient-to-r text-black bg-clip-text">ACCOUNT</span>
              </span>
              <div
                className="absolute left-0"
                style={{
                  bottom: '-0.5rem',
                  width: 'clamp(80px, 25vw, 150px)',
                  height: 'clamp(2px, 0.5vw, 4px)',
                  backgroundColor: 'black',
                }}
              />
            </h2>
          </div>
          {/* Right Side */}
          <div
            className="md:w-2/3 p-8 lg:p-12 flex flex-col justify-center"
            style={{ borderRadius: '20px' }}
          >
            <div className="flex items-center mb-8 justify-center md:justify-start">
              <img 
                src="/images/logo.png"
                alt="Cashmate Logo" 
                style={{
                  width: 'clamp(60px, 10vw, 94px)',
                  height: 'clamp(60px, 10vw, 94px)',
                  marginRight: 'clamp(10px, 2vw, 20px)',
                }}
              />
              <h3
                className="font-normal text-white"
                style={{ 
                  fontFamily: 'Century Gothic',
                  fontWeight: 340,
                  fontSize: 'clamp(24px, 5vw, 34px)',
                  lineHeight: 'clamp(30px, 6vw, 40px)',
                  letterSpacing: '0.3em',
                  textAlign: 'center',
                }}
              >
                CASHMATE
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 flex flex-col items-center">
              {['email', 'password', 'con_pass'].map((field, index) => {
                const isPasswordField = field === 'password';
                const isReconfirmField = field === 'con_pass';
                const show = isPasswordField ? showPassword : isReconfirmField ? showConfirmPassword : false;

                return (
                  <div key={index} className="w-full flex justify-center">
                  <div className="relative w-[90%] max-w-[500px] mobile-input">
                    <div className="absolute inset-y-0 left-2 md:left-3 flex items-center pointer-events-none">
                      {field === 'email' && (
                        <img
                          src={mailIcon}
                          alt="Mail Icon"
                          style={{
                            width: '20px',
                            height: '20px',
                          }}
                        />
                      )}
                      {(field === 'password' || field === 'con_pass') && (
                        <img
                          src={passwordIcon}
                          alt="Password Icon"
                          style={{
                            width: '20px',
                            height: '20px',
                          }}
                        />
                      )}
                    </div>

                    {(field === 'password' || field === 'con_pass') && (
                      <button type="button" onClick={() => setShowPassword(prev => !prev)} className="absolute inset-y-0 right-2 md:right-3 flex items-center">
                        <img
                          src={showPassword ? hideIcon : showIcon}
                          alt="Toggle visibility"
                          className="w-6 h-6"
                        />
                      </button>
                    )}

                    <input
                      id={field}
                      name={field}
                      type={field === 'email' ? 'text' : (field === 'password' ? (showPassword ? 'text' : 'password') : (showConfirmPassword ? 'text' : 'password'))}
                      required
                      className="w-full text-white rounded-full border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:bg-gray-500 placeholder:text-black text-xs sm:text-sm py-2 px-10"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.35)',
                        fontFamily: 'Century Gothic',
                        letterSpacing: '0.1em',
                        paddingLeft: '2.5rem',
                        paddingRight: '2.5rem',
                        fontSize: '12px',
                        '@media (min-width: 640px)': {
                          fontSize: '14px'
                        },
                        paddingTop: 'clamp(10px, 2vw, 14px)',
                        paddingBottom: 'clamp(10px, 2vw, 14px)'
                      }}
                      placeholder={
                        field === 'email'
                          ? 'E-MAIL'
                          : field === 'password'
                            ? 'PASSWORD'
                            : 'RE-CONFIRM PASSWORD'
                      }
                      value={formData[field]}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                );
              })}
              <div className="w-[90%] max-w-[500px] flex items-center">
  <label className="flex items-center gap-2 text-xs md:text-sm text-white">
    <input
      type="checkbox"
      className="mt-1"
      checked={agreed}
      onChange={(e) => setAgreed(e.target.checked)}
    />
    <span>
      I agree to the{' '}
      <a
        href="/CashMate_Terms_Condition_9-7-2025.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="text-purple-400 underline hover:text-purple-300"
      >
        Terms and Conditions 
      </a>
      <span className="text-white/80"> & </span>
      <a
        href="/CashMate_Privacy_Policy_9-7-2025.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="text-purple-400 underline hover:text-purple-300"
      >
        Privacy Policy
      </a>
    </span>
  </label>
</div>


              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}

<button
  type="submit"
  disabled={loading || !agreed}
  className={`w-[80%] sm:max-w-[500px] py-2 px-4 rounded-full text-white 
    ${loading || !agreed ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#392954] hover:bg-[#392954]/80'}
    backdrop-blur-md hover:scale-105 transition-all duration-300 font-['Century_Gothic'] text-sm md:text-base md:py-3`}
  style={{
    letterSpacing: '0.3em',
  }}
>


                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating account...
                  </div>
                ) : (
                  "CREATE ACCOUNT"
                )}
              </button>
              <Link to="/login" className="text-white/80 hover:text-white transition-colors font-['Century_Gothic']">
                <p
                  className="mt-4 font-['Century_Gothic'] text-purple-400 text-center text-xs md:text-sm"
                  style={{ letterSpacing: '0.1em' }}
                >
                  ALREADY HAVE AN ACCOUNT?                
                </p>
              </Link>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
